import React, { useEffect, useState } from 'react';
import { Button, Table, Select, InputNumber, DatePicker, Form, message } from 'antd';
import { Plus } from 'lucide-react';
import { openNotificationError, openNotificationSuccess } from '../../../Components/Notification/Notification';
import AxiosCaller from '../../../authenticatie/AxiosCaller';
import dayjs from 'dayjs';
import GetUserInfo from '../../authenticatie/GetUserInfo';

const { Option } = Select;

const ExtraProductToevoegenScreen = () => {
  const { get, post } = AxiosCaller();
  const [ingredienten, setIngredienten] = useState([]);
  const [newProductRows, setNewProductRows] = useState([]);
  const [klassen, setKlassen] = useState([]);
  const [userId, setUserId] = useState('');
  const [form] = Form.useForm(); // Create a form instance

  useEffect(() => {
    fetchIngredienten();
    fetchKlassen();
    setUserId(GetUserInfo()._id);
  }, []);

  const fetchIngredienten = async () => {
    try {
      const response = await get('/product');
      setIngredienten(response.data);
    } catch (error) {
      console.error(error);
      openNotificationError("Fout bij ophalen van producten", error);
    }
  };

  const fetchKlassen = async () => {
    try {
      const response = await get('/klas');
      setKlassen(response.data);
    } catch (error) {
      console.error(error);
      openNotificationError("Fout bij ophalen van de klassen", error);
    }
  };


  const handleAddProductRow = () => {
    const newRow = { 
      key: Date.now(), 
      product: '', 
      aantal: 1, 
      klas: '', 
      kookDatum: null, 
      leerkracht: userId 
    };
    setNewProductRows(prevRows => [...prevRows, newRow]);
  };

  const handleProductChange = (value, rowKey) => {
    const selectedProduct = ingredienten.find((product) => product._id === value);
    if (!selectedProduct) return;

    setNewProductRows(prevRows => 
      prevRows.map(row => 
        row.key === rowKey ? { ...row, product: selectedProduct._id } : row
      )
    );
  };

  const handleAantalChange = (value, rowKey) => {
    setNewProductRows(prevRows => 
      prevRows.map(row => 
        row.key === rowKey ? { ...row, aantal: String(value) } : row
      )
    );
  };

  const handleKlasChange = (value, rowKey) => {
    setNewProductRows(prevRows => 
      prevRows.map(row => 
        row.key === rowKey ? { ...row, klas: value } : row
      )
    );
  };

  const handleKookdatumChange = (date, dateString, rowKey) => {
    setNewProductRows(prevRows => 
      prevRows.map(row => 
        row.key === rowKey ? { ...row, kookDatum: date ? dayjs(date).format('YYYY-MM-DD') : null } : row
      )
    );
  };

  const handleSubmit = async () => {
    form.validateFields()
      .then( async() => {

        const formIsValid = newProductRows.every(
          (row) => row.product && row.aantal && row.klas && row.kookDatum
        );

        if (!formIsValid) {
          message.error("Alle velden moeten ingevuld zijn.");
          return;
        }


        
        await addBestellijnen(newProductRows)
     
      })
      .catch((error) => {
        console.error("Validation failed:", error);
      });
  };

  const addBestellijnen = async (bestellijnen) => {
    try {
        // Use map to return an array of promises
        const addBestelijnPromises = bestellijnen.map(async (bestellijn) => {
            await addBestelijn(bestellijn);
        });

        // Wait for all the promises to resolve
        await Promise.all(addBestelijnPromises);

        openNotificationSuccess("Producten succesvol toegevoegd");
        setNewProductRows([]);
    } catch (error) {
        setNewProductRows([]);
        openNotificationError("Fout bij toevoegen producten", error);
    }
};

const addBestelijn = async (bestellijn) => {
    try {
        await post('/bestellijn/manual', bestellijn);
    } catch (error) {
        openNotificationError("Fout bij toevoegen product");
        console.error(error);
        throw error; // Ensure the error is thrown to be caught in the outer try-catch block
    }
};

  const handleDeleteRow = (rowKey) => {
    // Remove row with the specified key
    setNewProductRows(prevRows => prevRows.filter(row => row.key !== rowKey));
  };

  // Add this function to disable past dates
const disablePastDates = (current) => {
  // Disable dates before today
  return current && current < dayjs().startOf('day');
};



  return (
    <div className='Screen-container'>
      <div className='space-between'>
        <h1 className='title'>Product toevoegen</h1>
        <Button onClick={handleAddProductRow} type="primary" className='button'>
          <Plus /> Nieuw Product toevoegen
        </Button>
      </div>
      
      <Form form={form} onFinish={handleSubmit}>
        <Table
          rowKey="key" // Use the key field for React's tracking
          columns={[
            { 
              title: 'Producten', 
              dataIndex: 'product', 
              key: 'product',
              render: (text, record) => (
                <Form.Item
                  name={`product_${record.key}`}
                  initialValue={record.product}
                  rules={[{ required: true, message: 'Selecteer een product!' }]}>
                  <Select
                    showSearch
                    value={record.product}
                    onChange={(value) => handleProductChange(value, record.key)}
                    placeholder="Selecteer product"
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      const productName = option.children.toString().toLowerCase();
                      return productName.includes(input.toLowerCase());
                    }}
                  >
                    {ingredienten.map((product) => (
                      <Option key={product._id} value={product._id}>
                        {product.naam}
                        {product.eenheid && ` (1 ${product.eenheid.naam})`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              ),
            },
            { 
              title: 'Aantal', 
              dataIndex: 'aantal', 
              key: 'aantal',
              render: (text, record) => (
                <Form.Item
                  name={`aantal_${record.key}`}
                  initialValue={record.aantal}
                  rules={[{ required: true, message: 'Vul het aantal in!' }]}>
                  <InputNumber
                    value={record.aantal}
                    onChange={(value) => handleAantalChange(value, record.key)}
                    min={1}
                    placeholder="Aantal"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              ),
            },
            {
              title: 'Klas',
              dataIndex: 'klas',
              key: 'klas',
              render: (text, record) => (
                <Form.Item
                  name={`klas_${record.key}`}
                  initialValue={record.klas}
                  rules={[{ required: true, message: 'Selecteer een klas!' }]}>
                  <Select
                    value={record.klas}
                    onChange={(value) => handleKlasChange(value, record.key)}
                    placeholder="Selecteer klas"
                    style={{ width: '100%' }}>
                    {klassen.map((klas) => (
                      <Option key={klas._id} value={klas._id}>
                        {klas.naam}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              ),
            },
            {
              title: 'Kookdatum',
              dataIndex: 'kookdatum',
              key: 'kookdatum',
              render: (text, record) => (
                <Form.Item
                  name={`kookdatum_${record.key}`}
                  initialValue={record.kookDatum ? dayjs(record.kookDatum) : null}
                  rules={[{ required: true, message: 'Selecteer een kookdatum!' }]}>
                  <DatePicker
                    value={record.kookDatum ? dayjs(record.kookDatum) : null}
                    onChange={(date, dateString) => handleKookdatumChange(date, dateString, record.key)}
                    placeholder="Kookdatum"
                    style={{ width: '100%' }}
                    disabledDate={disablePastDates}
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              ),
            },
            { 
              title: 'Acties', 
              dataIndex: 'acties', 
              key: 'acties',
              render: (text, record) => (
                <Button 
                  onClick={() => handleDeleteRow(record.key)} 
                  danger>
                  Verwijderen
                </Button>
              )
            },
          ]}
          dataSource={newProductRows}
          pagination={false}
          scroll={{ y: 0.5 * window.innerHeight }}
        />
        <Button type="primary" htmlType="submit" className='button margin-top'>
          Opslaan
        </Button>
      </Form>
    </div>
  );
};

export default ExtraProductToevoegenScreen;